import { marked } from 'marked';
import { useCallback, useRef } from 'react';

interface UseTypewriterParams {
  streamingBufferRef: React.MutableRefObject<string>;
  displayedLengthRef: React.MutableRefObject<number>;
  streamingMessageRef: React.MutableRefObject<HTMLDivElement | null>;
  scrollToBottom: () => void;
  shouldAnimateRef: React.MutableRefObject<boolean>;
}

const CHARS_PER_UPDATE = 5;
const UPDATE_INTERVAL = 32;

// Close unclosed markdown constructs to prevent raw syntax from flashing during streaming
const closeUnclosedMarkdown = (text: string): string => {
  let result = text;

  // Count ``` and close if odd
  const fenceMatches = result.match(/```/g);
  if (fenceMatches && fenceMatches.length % 2 !== 0) {
    result += '\n```';
  }

  // Count ** and close if odd
  const boldMatches = result.match(/\*\*/g);
  if (boldMatches && boldMatches.length % 2 !== 0) {
    result += '**';
  }

  // Count single ` (excluding ```) and close if odd
  const withoutFences = result.replace(/```/g, '');
  const backtickMatches = withoutFences.match(/`/g);
  if (backtickMatches && backtickMatches.length % 2 !== 0) {
    result += '`';
  }

  return result;
};

// Configure marked for safe HTML output
marked.setOptions({
  breaks: true,
  gfm: true,
});

export const useTypewriter = ({
  streamingBufferRef,
  displayedLengthRef,
  streamingMessageRef,
  scrollToBottom,
  shouldAnimateRef,
}: UseTypewriterParams) => {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isCancelledRef = useRef(false);

  const startTypewriter = useCallback(() => {
    isCancelledRef.current = false;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      if (isCancelledRef.current) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        return;
      }

      const buffer = streamingBufferRef.current;
      const currentDisplayedLength = displayedLengthRef.current;

      if (currentDisplayedLength < buffer.length && shouldAnimateRef.current) {
        const charsToAdd = Math.min(CHARS_PER_UPDATE, buffer.length - currentDisplayedLength);
        const newLength = currentDisplayedLength + charsToAdd;
        displayedLengthRef.current = newLength;

        if (streamingMessageRef.current) {
          const text = buffer.slice(0, newLength);
          // Close any unclosed markdown constructs, then convert to HTML
          const safeText = closeUnclosedMarkdown(text);
          streamingMessageRef.current.innerHTML = marked.parse(safeText) as string;
        }

        scrollToBottom();
      }
    }, UPDATE_INTERVAL);
  }, [streamingBufferRef, displayedLengthRef, streamingMessageRef, scrollToBottom, shouldAnimateRef]);

  const stopTypewriter = useCallback(() => {
    isCancelledRef.current = true;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    displayedLengthRef.current = 0;
  }, [displayedLengthRef]);

  const waitForTypewriterCompletion = useCallback((): Promise<void> => {
    return new Promise<void>((resolve) => {
      const maxWaitTime = 30000;
      const startTime = Date.now();

      const checkComplete = () => {
        if (isCancelledRef.current) {
          resolve();
          return;
        }

        if (Date.now() - startTime > maxWaitTime) {
          resolve();
          return;
        }

        if (displayedLengthRef.current >= streamingBufferRef.current.length && streamingBufferRef.current.length > 0) {
          setTimeout(() => resolve(), 50);
        } else {
          setTimeout(checkComplete, 50);
        }
      };
      checkComplete();
    });
  }, [displayedLengthRef, streamingBufferRef]);

  return {
    startTypewriter,
    stopTypewriter,
    waitForTypewriterCompletion,
  };
};
